import type React from 'react';
import useSWR from 'swr';
import { ClipboardCheck, ListChecks, MessageSquareText, ScanLine, Star, TrendingUp, UsersRound } from 'lucide-react';
import { listCameras } from '@/api/cameras';
import type { Paginated } from '@/api/types';
import { useZoneFeatures } from '@/hooks/useZoneFeatures';

export interface FunctionSettingsItem {
  to: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
}

// «Настройки функций» (Алексей 04.11 #4031): все подключённые функции — вкладками, в порядке
// вкладок раздела «Данные»: выкладка/столы (разметка), очередь, задачи, разговоры, визиты,
// отзывы, план продаж. Чеки (подключение ОФД) — пункт «Интеграции» (#4036).
const FUNCTION_ITEMS = {
  zoning: { to: '/management/zoning', labelKey: 'sidebar.zoning', icon: ScanLine },
  // «Очереди и сотрудники» (#1798): порог очереди и приход к открытию.
  queuesStaff: { to: '/management/queues-staff', labelKey: 'sidebar.queuesStaff', icon: UsersRound },
  checklists: { to: '/management/checklists', labelKey: 'sidebar.checklists', icon: ListChecks },
  speech: { to: '/management/speech', labelKey: 'sidebar.speech', icon: MessageSquareText },
  visits: { to: '/management/visits', labelKey: 'sidebar.visits', icon: ClipboardCheck },
  reviews: { to: '/management/reviews', labelKey: 'sidebar.reviews', icon: Star },
  // «План продаж» (Алексей 22.10, #1941/#1942).
  salesPlan: { to: '/management/sales-plan', labelKey: 'sidebar.salesPlan', icon: TrendingUp },
} satisfies Record<string, FunctionSettingsItem>;

export const FUNCTION_SETTINGS_PATHS: string[] = Object.values(FUNCTION_ITEMS).map(i => i.to);

/** Функции, включённые у филиала, — один источник для сайдбара и вкладок «Настроек функций». */
export function useFunctionSettingsItems(zoneId: string | null | undefined): FunctionSettingsItem[] {
  const { features } = useZoneFeatures(zoneId ?? null);
  // Мониторинг зон не «включается» — он появляется с первой камерой.
  const { data: camerasData } = useSWR<Paginated<{ id: string }>>(
    zoneId ? ['zone-cameras-any', zoneId] : null,
    async () => (await listCameras(1, zoneId!)).data as Paginated<{ id: string }>,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const hasCameras = (camerasData?.results.length ?? 0) > 0;
  return [
    ...(hasCameras ? [FUNCTION_ITEMS.zoning] : []),
    ...(hasCameras || features?.staff_arrival?.enabled ? [FUNCTION_ITEMS.queuesStaff] : []),
    ...(features?.checklists?.enabled ? [FUNCTION_ITEMS.checklists] : []),
    ...(features?.speech?.enabled ? [FUNCTION_ITEMS.speech] : []),
    ...(features?.visits?.enabled ? [FUNCTION_ITEMS.visits] : []),
    ...(features?.reviews?.enabled ? [FUNCTION_ITEMS.reviews] : []),
    ...(features?.sales_plan?.enabled ? [FUNCTION_ITEMS.salesPlan] : []),
  ];
}
